import { CoreConfig } from "../../../config/core-config.type";

/** The Keycloak tokens in the shape keycloak.init() takes them back in. */
export interface StoredTokens {
  token?: string;
  refreshToken?: string;
  idToken?: string;
}

/**
 * localStorage access for the Keycloak tokens.
 *
 * Shared with the APP_INITIALIZER, which runs before AuthService is constructed and
 * still has to hand the stored tokens to keycloak.init().
 */
export class TokenStorage {
  constructor(private readonly _config: CoreConfig) {}

  public read(): StoredTokens {
    return {
      token: this.readItem(this._config.storage.accessToken),
      refreshToken: this.readItem(this._config.storage.refreshToken),
      idToken: this.readItem(this._config.storage.idToken),
    };
  }

  public write(tokens: StoredTokens): void {
    this.writeItem(this._config.storage.accessToken, tokens.token);
    this.writeItem(this._config.storage.refreshToken, tokens.refreshToken);
    this.writeItem(this._config.storage.idToken, tokens.idToken);
  }

  public clear(): void {
    for (const name of [
      this._config.storage.accessToken,
      this._config.storage.refreshToken,
      this._config.storage.idToken,
    ]) {
      try {
        localStorage.removeItem(this.key(name));
      } catch {
        // Nothing to recover: the token is gone either way.
      }
    }
  }

  private key(name: string): string {
    return this._config.storage.localStoragePrefix + name;
  }

  // ⚠ Safari in private mode throws on both read and write, so every access is
  // guarded - an unguarded read here would take down the APP_INITIALIZER.
  private readItem(name: string): string | undefined {
    try {
      return localStorage.getItem(this.key(name)) ?? undefined;
    } catch {
      return undefined;
    }
  }

  private writeItem(name: string, value?: string): void {
    if (!value) {
      return;
    }

    try {
      localStorage.setItem(this.key(name), value);
    } catch {
      console.error("Couldn't save token");
    }
  }
}
